'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineEvent {
  id: number;
  year: string;
  title: string;
  description: string;
  icon: string;
}

interface CompanyTimelineProps {
  className?: string;
}

const CompanyTimeline = ({ className = '' }: CompanyTimelineProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const timelineEvents: TimelineEvent[] = [
    {
      id: 1,
      year: '2010',
      title: 'Fondation',
      description: 'Création de Services Rouissi au Locle avec une équipe de 3 personnes passionnées par l\'excellence du service.',
      icon: 'RocketLaunchIcon'
    },
    {
      id: 2,
      year: '2013',
      title: 'Expansion Résidentielle',
      description: 'Lancement des services aux particuliers et développement d\'une clientèle fidèle de plus de 100 foyers.',
      icon: 'HomeIcon'
    },
    {
      id: 3,
      year: '2016',
      title: 'Services Commerciaux',
      description: 'Extension vers le secteur commercial avec des contrats d\'entretien pour bureaux et commerces locaux.',
      icon: 'BuildingOfficeIcon'
    },
    {
      id: 4,
      year: '2019',
      title: 'Certifications Qualité',
      description: 'Obtention des certifications suisses de qualité et formation avancée de toute l\'équipe.',
      icon: 'AcademicCapIcon'
    },
    {
      id: 5,
      year: '2022',
      title: 'Innovation Écologique',
      description: 'Adoption de produits et techniques de nettoyage 100% écologiques et respectueux de l\'environnement.',
      icon: 'SparklesIcon'
    },
    {
      id: 6,
      year: '2026',
      title: 'Leadership Régional',
      description: 'Reconnaissance comme leader du nettoyage professionnel dans la région avec une équipe de 25+ professionnels.',
      icon: 'TrophyIcon'
    }
  ];

  if (!isHydrated) {
    return (
      <section className={`py-16 bg-surface ${className}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="h-10 bg-muted rounded w-64 mx-auto mb-4"></div>
              <div className="h-6 bg-muted rounded w-96 mx-auto"></div>
            </div>
            <div className="space-y-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-card rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-12 md:py-16 bg-surface ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Notre Parcours
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Une histoire de croissance, d'innovation et d'engagement envers l'excellence
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border hidden md:block"></div>

            <div className="space-y-6 md:space-y-8">
              {timelineEvents.map((event, index) => (
                <div
                  key={event.id}
                  className="relative flex items-start space-x-4 md:space-x-6"
                >
                  {/* Timeline dot */}
                  <div className="hidden md:flex flex-shrink-0 w-16 h-16 bg-primary rounded-full items-center justify-center shadow-lg z-10">
                    <Icon
                      name={event.icon as any}
                      size={28}
                      className="text-primary-foreground"
                      variant="solid"
                    />
                  </div>

                  {/* Content card */}
                  <div className="flex-1 bg-card p-4 md:p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center space-x-3 md:space-x-4 mb-3">
                      <div className="md:hidden flex-shrink-0 w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                        <Icon
                          name={event.icon as any}
                          size={20}
                          className="text-primary-foreground"
                          variant="solid"
                        />
                      </div>
                      <span className="text-xl md:text-2xl font-heading font-bold text-accent">
                        {event.year}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2 break-words">
                      {event.title}
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed break-words">
                      {event.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyTimeline;